import { notify, useModal } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalStorage } from 'react-use'

import { useUpdateOrganizationSettings, useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../config/queryClient'
import { LocalStorageKeys } from '../../../../../enums/localStorageKeys'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { useForm, useSegment } from '../../../../../hooks'
import { useEmberTransitionTo } from '../../../../../hooks/routing/useEmberTransitionTo'
import { useOrganizationViewUrl } from '../../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../../utils/routing/reactRoute'
import { useInitiateExternalInvoicesSync } from '../../../hooks/useInitiateExternalInvoicesSync'
import { defaultValues, ExternalInvoicesSyncForm, getValidationSchema } from '../utils/formData'
import { getInitiateSyncPayload } from '../utils/getInitiateSyncPayload'
import { notifySyncStartError } from '../utils/notifySyncStartError'

interface ContextState {
  closeModal: () => void
  isProcessing: boolean
  submitForm: () => void
}

const ExternalInvoicesSyncModalContext = createContext<ContextState | undefined>(undefined)

interface ExternalInvoicesSyncModalContextProps {
  children: ReactNode
  modalId: string
}

export const ExternalInvoicesSyncModalContextProvider = ({
  children,
  modalId,
}: ExternalInvoicesSyncModalContextProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { transitionTo } = useEmberTransitionTo()
  const { organization } = useUserOrganization()
  const { url: dashboardUrl } = useOrganizationViewUrl(reactRoute.organization.dashboard.getMainRoute())
  const { update: updateOrganizationSettings } = useUpdateOrganizationSettings()
  const { Form, handleSubmit } = useForm({
    defaultValues,
    validationSchema: getValidationSchema(),
  })
  const { close: closeModal } = useModal(modalId)
  const [, , clearIsSyncProgressTopBarClosed] = useLocalStorage(
    `${LocalStorageKeys.HideExternalInvoicesSyncProgressTopBar}-${organization?.id}`,
  )

  const organizationId = organization?.id

  const { initiateSync, isProcessing } = useInitiateExternalInvoicesSync({
    onSuccess: () => {
      clearIsSyncProgressTopBarClosed()
      if (organizationId) {
        updateOrganizationSettings({
          key: 'externalInvoicesSyncShowTopBar',
          organizationId,
          value: '1',
        })
      }
      queryClient.invalidateQueries(QueryKeys.ExternalInvoicesSync)
      notify({
        id: NotificationKeys.ExternalInvoicesSyncStarted,
        message: t('external_invoices_sync_modal.sync_success'),
        variant: 'success',
      })
      track('xxx-sales-HistoricalSync Sync successfully initiated')

      closeModal()

      if (dashboardUrl) {
        transitionTo(dashboardUrl)
      }
    },
    onError: () => {
      notifySyncStartError(t)
    },
  })

  const externalCompanyId = parseInt(organization?.externalId || '', 10) || undefined
  const countryId = organization?.countryId

  const handleFormSubmit = useCallback(
    (values: ExternalInvoicesSyncForm) => {
      if (!organizationId || !countryId || !externalCompanyId) {
        notifySyncStartError(t)
        return
      }

      const payload = getInitiateSyncPayload({ countryId, externalCompanyId, formValues: values, organizationId })

      initiateSync(payload)
    },
    [countryId, externalCompanyId, initiateSync, organizationId, t],
  )
  const submitForm = useCallback(() => {
    handleSubmit(handleFormSubmit)()
  }, [handleFormSubmit, handleSubmit])

  return (
    <ExternalInvoicesSyncModalContext.Provider value={{ closeModal, isProcessing, submitForm }}>
      <Form>{children}</Form>
    </ExternalInvoicesSyncModalContext.Provider>
  )
}

export const useExternalInvoicesSyncModal = () => {
  const context = useContext(ExternalInvoicesSyncModalContext)

  if (!context) {
    throw new Error('ExternalInvoicesSyncModalContextProvider is missing in the module!')
  }

  return context
}
